import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Drawer, Button, Message, Modal, Tag, Space, Form } from '@bedrock/components';
import { useSForm } from '@bedrock/eed-react';
import VerticalLineTitle from '#components/VerticalLineTitle';
import { useRequest, useMemoizedFn } from 'ahooks';
import withFunctionCall from '@mpc/common/src/utils/withFunctionCall';
import { $sType } from '@mpc/common/src/utils/sType';
import type { TagColor } from '@bedrock/components/lib/Tag';
import RefundFormModel from '../FormModel';
import { Z_INDEX } from '../../../utils/zIndex';
import { refundSubmit, refundDraft, refundDetail, refundAbandon } from '../api';
import { useRefundOptimizerOptions } from '../useOptimizerOptions';
import { RefundStatusTag } from '../Drawer/RefundStatusTag';
import { RefundActions } from '../Drawer/Actions';
import type { RefundOrderDetailVo } from 'services/api';
import type { MockRefundOrder } from '../mock';
import {
  REFUND_STATE_MAP,
  REFUND_INITIATOR_ROLE_LABEL,
  REFUND_REGION_LABEL,
} from '../constants';
import { buildDetailFromRecord, buildDetailFromDraft } from './buildDetailData';
import { useRefundDrawerWidth } from './useDrawerWidth';

/**
 * 后端 JournalVO.state 是中文 desc（RefundFlowState.getDesc()，如"待优化师确认"）而非枚举名，
 * 反查 REFUND_STATE_MAP 还原枚举名，供"退回来源节点"判断（_CONFIRM 后缀）。
 */
export type FormDrawerMode = 'create' | 'edit' | 'view';

export interface FormDrawerProps {
  mode: FormDrawerMode;
  visible: boolean;
  onCancel: () => void;
  /** 操作成功回调（命令式 show() 后暴露给调用方刷新列表；create/edit 参数为新退款单 id，view 无参） */
  onSuccess?: (id?: number) => void;
  /** create 模式：基于二级单发起（草稿接口预填只读字段），调用方必须传 */
  l2DemandId?: number;
  /** create 模式：一级单 id（需求单号）。优化师下拉按产品反查时优先用一级单（必有产品），不传回落 l2 */
  demandId?: number;
  /** create 模式：三级单已生成时传，无三级单则不传 */
  l3DemandId?: number;
  /** edit 模式：待修改的退款单（真实详情 VO 或 mock 结构兼容），退回后 resubmit 复用原单号（T19） */
  editRecord?: RefundOrderDetailVo | MockRefundOrder | null;
  /** view 模式：退款单 id，拉详情只读展示 */
  orderId?: number;
  /** view 模式：外包端精简展示（不展示废弃 + 过滤预算/执行日期区间），接口走外包路径 */
  isExternal?: boolean;
}

/**
 * 退款单抽屉（单个发起/修改/详情，合并 CreateRefundModal + RefundDetailDrawer + OuterDrawer）
 *
 * 范式锚点：PrePayment/Drawer/index.tsx（详情/发起/审批编辑统一 Drawer）
 * 设计依据：frontend-design.md §3-§6 + 2026-08-05 需求确认（不需要 Tabs/状态进度，统一直接渲染表单）
 *
 * 三 mode 差异：
 * - create：可编辑表单 + 草稿接口预填只读字段（budget/执行日期/渠道/币种/l2/l3），footer 取消+提交
 * - edit：可编辑表单 + 退回后 resubmit（带 refund_order_id 复用原单号），footer 取消+提交
 * - view：只读表单（view:true）+ RefundActions footer（确认/退回/废弃）；isExternal 触发外包精简
 *   （过滤预算/执行日期 + 供应商确认态 PENDING_SUPPLIER_CONFIRM 附件可编辑）
 *
 * 宽度：useRefundDrawerWidth（80% 视口 min 960）；表单复用 RefundFormModel（create/edit → view:false，
 * view → view:true），detailData 由 buildDetailData 共享映射生成。
 * withFunctionCall 包裹 → 命令式 `FormDrawer.show({ mode, ... })`。
 */
export const FormDrawerView: React.FC<FormDrawerProps> = ({
  mode,
  visible,
  onCancel,
  onSuccess,
  l2DemandId,
  demandId,
  l3DemandId,
  editRecord,
  orderId,
  isExternal,
}) => {
  const isView = mode === 'view';
  const [loading, setLoading] = useState(false);
  const [draftData, setDraftData] = useState<Record<string, any> | null>(null);
  const { form, formData, updateFormData } = useSForm();

  // 优化师选项（FR-14）：非 view 模式按需求单 id 取真实产品优化师（create 优先一级单 demandId，
  // 回落 l2；edit 用退款单 l2，后端 demandId 统一按 Demand 反查产品）。经 renderData.optionMap.optimizer
  // 注入 FormModel optimizers（无则回落 mock）
  const optimizerDemandIds = useMemo(() => {
    if (mode === 'create') {
      if (demandId) return [demandId];
      if (l2DemandId) return [l2DemandId];
      return [];
    }
    if (mode === 'edit' && editRecord) {
      const id = (editRecord as any)?.l2_demand_id;
      return typeof id === 'number' && id > 0 ? [id] : [];
    }
    return [];
  }, [mode, demandId, l2DemandId, editRecord]);
  const optimizerOptions = useRefundOptimizerOptions(optimizerDemandIds, visible && !isView);

  // create 模式：打开时按 l2（或 l3）调草稿接口预填只读字段（替代 mock 占位，前端不再硬编码）
  useEffect(() => {
    if (!visible) return;
    if (mode === 'create' && l2DemandId) {
      refundDraft({
        l2_demand_id: l2DemandId,
        l3_demand_id: l3DemandId ?? undefined,
      })
        .then((res: any) => setDraftData(res?.data ?? null))
        .catch(() => setDraftData(null));
    } else if (mode === 'create') {
      setDraftData(null);
    }
  }, [visible, mode, l2DemandId, l3DemandId]);

  // view 模式：拉详情
  const { data: detail, loading: detailLoading, run } = useRequest(
    () => refundDetail(orderId!).then((r: any) => r?.data),
    { refreshDeps: [orderId], ready: !!orderId }
  );
  useEffect(() => {
    if (visible && isView && orderId) run();
  }, [visible, isView, orderId, run]);

  // 退回来源节点：后端详情接口已返回 reject_from_state（镜像充值 RechargeConfirmView，反查 journal 落在后端 getRejectFromState）。
  // 前端直接取详情 VO 的 reject_from_state，避免额外调 journals + 中文 desc 反查（2026-08-26 对齐充值实现）。
  const rejectFromState =
    mode === 'view'
      ? detail?.reject_from_state ?? ''
      : (editRecord as any)?.reject_from_state ?? '';

  // 操作权限（确认/退回/废弃按钮显隐，后端详情接口返回，按当前用户是否待办人 + 状态节点计算；镜像充值 RechargeConfirmView）。
  const permitAction =
    mode === 'view' ? detail?.permit_action : (editRecord as any)?.permit_action;

  const detailData = useMemo(() => {
    if (mode === 'edit' && editRecord) return buildDetailFromRecord(editRecord);
    if (mode === 'view') return buildDetailFromRecord((detail ?? {}) as any);
    return buildDetailFromDraft(draftData, l2DemandId, l3DemandId);
  }, [mode, editRecord, detail, draftData, l2DemandId, l3DemandId]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue(detailData);
    }
  }, [visible, detailData, form]);

  const handleOk = useMemoizedFn(async () => {
    try {
      const formValues = await form.validateFields();
      setLoading(true);
      const submitData = {
        l2_demand_id: formValues.l2_demand_id,
        l3_demand_id: formValues.l3_demand_id ?? null,
        amount: Number(formValues.amount),
        currency: formValues.currency || 'RMB',
        account_id_name: formValues.account_id_name || '',
        channel: formValues.channel || '',
        platform: formValues.platform || '',
        // 详情模型 optimizers（人员对象数组）→ 提交映射回 id 数组（MockRefundSubmitItem.optimizer_ids: number[]）
        optimizer_ids: (formValues.optimizers || [])
          .map((v: { user_id?: number } | number) =>
            typeof v === 'object' && v !== null ? v.user_id : v
          )
          .filter((id: unknown): id is number => typeof id === 'number'),
        // 退款说明/退款附件分开存储（formItemKeyMap 投影的扁平字段）
        refund_remark: formValues.refund_remark ?? '',
        refund_attachments: formValues.refund_attachments ?? [],
        // T19 退回后重新发起（resubmit）：edit 模式传原退款单 id，后端复用原单号走 UPDATE
        refund_order_id:
          mode === 'edit' && editRecord
            ? ((editRecord as any).id ?? (editRecord as any).order_id)
            : undefined,
        // 发起方角色：edit 保留原角色；create 用草稿返回的后端推断角色（镜像充值，后端 submit 也会覆盖防伪造）
        initiator_role:
          (editRecord as any)?.initiator_role ??
          (draftData as any)?.initiator_role ??
          'PLANNER',
      };
      if (mode === 'edit' && editRecord) {
        await refundSubmit(submitData, { silent: true });
        Message.success('退款单修改成功');
        onSuccess?.((editRecord as any).id ?? (editRecord as any).order_id);
      } else {
        const res = await refundSubmit(submitData, { silent: true });
        Message.success('退款单发起成功');
        onSuccess?.((res as any)?.data);
      }
      onCancel();
    } catch (e) {
      // 表单校验失败（e.errorFields 来自 validateFields）控件已标红，不弹；业务异常主动提示（避免依赖全局拦截器兜底）
      if (!(e as { errorFields?: unknown })?.errorFields) {
        Message.error((e as { message?: string })?.message || '退款单提交失败');
      }
    } finally {
      setLoading(false);
    }
  });

  // 待发起场景废弃（终止退款单，参考 RefundActions.handleAbandon）
  const handleAbandon = () => {
    const id = (editRecord as any)?.id ?? (editRecord as any)?.order_id;
    if (!id) return;
    Modal.confirm({
      title: '确认废弃该退款单？',
      // 高于 Drawer(zIndex 1030)，避免被抽屉盖住（rock-modal 默认 1000 < drawer）
      zIndex: Z_INDEX.modal,
      onOk: async () => {
        try {
          await refundAbandon(id, { silent: true });
          Message.success('已废弃');
          onSuccess?.();
          onCancel();
        } catch (err: unknown) {
          Message.error((err as { message?: string })?.message || '废弃失败');
        }
      },
    });
  };

  const width = useRefundDrawerWidth();

  // 供应商确认（FR-13）态：退款说明+附件可编辑（外包端 PENDING_SUPPLIER_CONFIRM）
  const isSupplierConfirm = isExternal && detailData.status === 'PENDING_SUPPLIER_CONFIRM';

  // itemCols：create/edit 与 view 的字段展示差异（view 外包端过滤预算/执行日期，供应商态附件可编辑）
  const itemCols = useMemo(() => {
    if (mode === 'view') {
      return [
        { formItemKey: 'l2_demand_id', view: true },
        { formItemKey: 'l3_demand_id', view: true },
        { formItemKey: 'refund_no', view: true },
        { formItemKey: '$refund_amount', view: true },
        { formItemKey: 'account_id_name', view: true },
        { formItemKey: 'channel', view: true },
        { formItemKey: 'platform', view: true },
        // 预算/执行日期：仅内包 view 展示；外包供应商端敏感字段不渲染（后端已过滤，字段也应排除，
        // 否则 itemCols view:false 会渲染成可编辑控件而非隐藏）
        ...(isExternal
          ? []
          : [
              { formItemKey: '$budget_amount', view: true },
              { formItemKey: 'execute_date_start', view: true },
            ]),
        { formItemKey: 'optimizers', view: true },
        {
          formItemKey: 'attachments',
          view: isSupplierConfirm ? false : true,
        },
      ];
    }
    return [
      { formItemKey: 'l2_demand_id', view: true },
      { formItemKey: 'l3_demand_id', view: true },
      { formItemKey: 'refund_no', view: true },
      { formItemKey: '$refund_amount', view: false },
      { formItemKey: 'account_id_name', view: false },
      // 投放渠道/合作平台：可编辑（POPO：仅特定需求类型显示 + 策划/优化师必填、供应商非必填，FormModel 内控制）
      { formItemKey: 'channel', view: false },
      { formItemKey: 'platform', view: false },
      // 预算/执行日期：仅内包发起显示（自动带入只读）；外包发起无需填写/展示（产品 2026-08-24 确认）
      ...($sType === 'outer'
        ? []
        : [
            { formItemKey: '$budget_amount', view: true },
            { formItemKey: 'execute_date_start', view: true },
          ]),
      // 优化师：仅供应商发起（外包 $sType=outer）显示；策划/优化师发起（内包）隐藏
      // （产品规则 2026-08-24：优化师字段在内部发起时不需要出现，仅供应商发起才需要填写）
      ...($sType === 'outer' ? [{ formItemKey: 'optimizers', view: false }] : []),
      // 退款说明/退款附件：发起（create/edit）不显示，仅供应商确认态 view 展示可编辑（FR-13）
    ];
  }, [mode, isExternal, isSupplierConfirm]);

  const renderForm = () => (
    <Form form={form} layout="vertical" onValuesChange={() => updateFormData()}>
      <RefundFormModel
        renderType="descriptions"
        renderData={{
          detailData,
          formData,
          optionMap: { optimizer: optimizerOptions },
        }}
        globalItemProps={{ view: isView }}
        itemCols={itemCols}
      />
    </Form>
  );

  const renderTitle = () => {
    if (mode === 'view') {
      return (
        <div className="flex flex-col gap-1">
          {/* 标题不展示单号（Design Feedback 2026-08-25）；「退款单」+ 状态 Tag + 需求类型 + 角色·区域同一行 */}
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold leading-[26px]">
              退款单
            </span>
            {detailData.status && <RefundStatusTag status={detailData.status} />}
            {detail?.demand_category_name && (
              <span className="inline-flex items-center rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600">
                {detail.demand_category_name}
              </span>
            )}
            {!isExternal && (
              <span className="text-xs text-gray-400">
                {REFUND_INITIATOR_ROLE_LABEL[detail?.initiator_role] ?? '--'} ·{' '}
                {REFUND_REGION_LABEL[detail?.region] ?? '--'}
              </span>
            )}
          </div>
        </div>
      );
    }
    const titleText = mode === 'edit' ? '修改退款单' : '发起退款单';
    const statusMeta = editRecord
      ? REFUND_STATE_MAP[(editRecord as any).status]
      : undefined;
    return (
      <Space size={12}>
        <span>{titleText}</span>
        {statusMeta && (
          <Tag
            size="small"
            text={statusMeta.label}
            color={`light-${statusMeta.tagColor}` as TagColor}
          />
        )}
        {editRecord && (
          <span className="text-xs text-gray-400">
            {REFUND_INITIATOR_ROLE_LABEL[(editRecord as any).initiator_role]} ·{' '}
            {REFUND_REGION_LABEL[(editRecord as any).region]}
          </span>
        )}
      </Space>
    );
  };

  const renderFooter = () => {
    if (mode === 'view') {
      const status = detailData.status;
      if (['COMPLETED', 'ABANDONED'].includes(status ?? '')) return null;
      // 外包端：仅供应商待发起/供应商确认可操作（编辑/确认），其他节点外包只读展示，不出现底部按钮
      if (
        isExternal &&
        !['PENDING_SUPPLIER_INITIATE', 'PENDING_SUPPLIER_CONFIRM'].includes(
          status ?? ''
        )
      ) {
        return null;
      }
      return (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="checked-neutral"
            size="large"
            onClick={onCancel}
            style={{ minWidth: 120 }}
          >
            取消
          </Button>
          <RefundActions
            orderId={orderId}
            permitAction={permitAction}
            onRefresh={() => {
              run();
              onSuccess?.();
            }}
            form={form}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          type="checked-neutral"
          size="large"
          onClick={onCancel}
          style={{ minWidth: 120 }}
        >
          取消
        </Button>
        {mode === 'edit' && permitAction?.can_abandon && (
          <Button
            danger
            size="large"
            onClick={handleAbandon}
            style={{ minWidth: 120 }}
          >
            废弃
          </Button>
        )}
        <Button
          type="primary"
          size="large"
          loading={loading}
          onClick={handleOk}
          style={{ minWidth: 120 }}
        >
          提交
        </Button>
      </div>
    );
  };

  return (
    <Drawer
      className={isView ? undefined : 'refund-detail-drawer'}
      visible={visible}
      onClose={onCancel}
      width={width}
      bodyClassName="bg-[#F0F1F2]"
      style={{ right: 0 }}
      zIndex={Z_INDEX.drawer}
      getContainer={false}
      maskStyle={{ backgroundColor: 'transparent' }}
      showMask
      destroyOnClose
      maskClosable={false}
      title={renderTitle()}
      footer={renderFooter()}
    >
      {mode === 'view' ? (
        detailLoading || !orderId ? (
          <div className="py-10 text-center text-gray-400">加载中…</div>
        ) : (
          <div className="flex flex-col gap-4 bg-[#f5f5f5]">
            <div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
              <VerticalLineTitle text="退款信息" style={{ marginBottom: 16 }} />
              {detail?.reject_reason && (
                <Alert
                  message={rejectFromState ? `由 ${REFUND_STATE_MAP[rejectFromState]?.label ?? rejectFromState} 退回` : '已退回'}
                  description={`退回原因：${detail.reject_reason}`}
                  type="warning"
                  showIcon
                  className="flex-1 mb-4"
                />
              )}
              {renderForm()}
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-4 bg-[#f5f5f5]">
          <div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
            <VerticalLineTitle text="退款信息" style={{ marginBottom: 16 }} />
            {/* edit 模式：有退回原因也展示（Design Feedback 2026-08-25，与 view 一致） */}
            {(editRecord as any)?.reject_reason && (
              <Alert
                message={rejectFromState ? `由 ${REFUND_STATE_MAP[rejectFromState]?.label ?? rejectFromState} 退回` : '已退回'}
                description={`退回原因：${(editRecord as any)?.reject_reason}`}
                type="warning"
                showIcon
                className="flex-1 mb-4"
              />
            )}
            {renderForm()}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default withFunctionCall(FormDrawerView);
